<template>
  <div v-show="!searchFilterStore.isCurrentFilterDefault">
    <div v-if="!isEditModeOn" class="container-edit-mode-off font-body-2">
      <div>{{ searchFilterStore?.currentSearchFilter?.name }}</div>
      <font-awesome-icon
        class="edit-button"
        :icon="['far', 'pen-to-square']"
        @click="turnEditModeOn"
      />
    </div>
    <div v-else class="container-edit-mode-on">
      <div :class="`px-5 ${isEmpty ? 'pt-5 pb-1' : 'py-5'}`">
        <input
          ref="inputElement"
          v-model="inputValue"
          :class="`input-box${isEmpty ? '--faulty' : ''}`"
          autofocus
          @keyup.enter="saveChanges"
          @input="handleInput"
        />
      </div>
      <span v-if="isEmpty" class="filter_name_section__error">{{
        $t("manage.mentionsAndPublications.form.requiredField")
      }}</span>
      <span v-else-if="isDuplicate" class="filter_name_section__error">
        {{ $t("appliedFilters.errMsg") }}
      </span>
      <div class="notice">
        {{ $t("appliedFilters.filterNameEditingNotice") }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Ref, ref, watchEffect } from "vue";
import { useSearchFiltersStore } from "~/store/searchFilter";
import SearchFilterAttributes from "~/types/attributes/SearchFilterAttributesType";

const searchFilterStore = useSearchFiltersStore();

const emit = defineEmits(["editing"]);

const isEditModeOn: Ref<boolean> = ref(false);
const inputValue: Ref<string> = ref("");
const inputElement: Ref<HTMLInputElement | null> = ref(null);
const isEmpty: Ref<boolean> = ref(false);
const isDuplicate: Ref<boolean> = ref(false);

const turnEditModeOn = () => {
  inputValue.value = searchFilterStore?.currentSearchFilter?.name ?? "";
  isEditModeOn.value = true;
};

const saveChanges = () => {
  if (isDuplicate.value) {
    return;
  }

  if (!inputValue.value) {
    isEmpty.value = true;
    return;
  }
  if (searchFilterStore?.currentSearchFilter) {
    searchFilterStore.currentSearchFilter.name = inputValue.value;
  }
  isEditModeOn.value = false;
};

const handleInput = (event: Event) => {
  isEmpty.value = false;
  inputValue.value = (event.target as HTMLInputElement).value;

  const hasDuplicate = searchFilterStore.getUserSearchFilters.some(
    (filter: SearchFilterAttributes) => filter.name === inputValue.value.trim(),
  );

  isDuplicate.value = hasDuplicate;
};

watchEffect(() => {
  if (inputElement.value) {
    inputElement.value.focus();
  }
});

watch(
  () => isEditModeOn.value,
  (newVal) => {
    emit("editing", newVal);
  },
);

watch(
  () => searchFilterStore.currentSearchFilter,
  (newFilter) => {
    if (newFilter) {
      inputValue.value = newFilter.name || "";
      isEditModeOn.value = false;
    }
  },
  { deep: true },
);
</script>

<style scoped lang="scss">
.container-edit-mode-off {
  @apply flex justify-between items-center h-14 mx-2 my-2.5 px-5 py-4 rounded-[0.625rem] border border-accent-200 rounded-[0.25rem];
}
.container-edit-mode-on {
  @apply flex flex-col w-full h-full;
}
.input-box {
  @apply w-full border border-accent-200 focus:border-accent-200 focus:outline-none rounded-[0.625rem] px-[0.62rem] py-2;
  &--faulty {
    @apply border-danger-300 w-full border focus:border-danger-300 focus:outline-none rounded-[0.625rem] px-[0.62rem] py-2;
  }
}
.notice {
  @apply bg-accent-100 w-full h-full px-5 py-2.5 rounded-b-xl text-accent-500 text-[0.8125rem];
}
.edit-button {
  @apply cursor-pointer text-accent-500 h-5 w-5;
}

.filter_name_section__error {
  @apply text-danger-600 ml-5 mb-5 text-sm;
}
</style>
